// «موقع العقار على الخريطة»: three ways to give the place of a property —
//   1) paste a Google Maps link (or coordinates)   2) my current location (GPS)   3) move a satellite map under a pin
// The chosen point is shown on a small satellite map before the form is sent.
import EditLocationAltIcon from '@mui/icons-material/EditLocationAlt';
import LinkIcon from '@mui/icons-material/Link';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { lazy, Suspense, useState } from 'react';

const MapCanvas = lazy(() => import('./MapCanvas'));

export interface MapPoint {
  lat: number;
  lng: number;
}

/** What the map needs from the app: the Mapbox token and a way to open short Google Maps links (on the server). */
export interface MapSupport {
  token: string | null;
  resolveLink: (url: string) => Promise<MapPoint | null>;
  /** where an empty map starts (the company's city); Amman by default */
  defaultCenter?: MapPoint;
  /** the company's work area [west, south, east, north]: an empty map shows it */
  bbox?: [number, number, number, number] | null;
}

type Lang = 'ar' | 'en';
const T = (lang: Lang, ar: string, en: string) => (lang === 'en' ? en : ar);
const AMMAN: MapPoint = { lat: 31.9539, lng: 35.9106 };

const NUM = String.raw`(-?\d{1,3}(?:\.\d+)?)`;
const valid = (lat: number, lng: number): MapPoint | null =>
  Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180 && !(lat === 0 && lng === 0)
    ? { lat: Math.round(lat * 1e6) / 1e6, lng: Math.round(lng * 1e6) / 1e6 }
    : null;

/** Coordinates written in a Google Maps link or typed as «lat, lng» (same rules as the server). */
export function pointFromText(text: string): MapPoint | null {
  let s = text.trim();
  try {
    s = decodeURIComponent(s);
  } catch {
    /* keep as is */
  }
  const tries = [
    new RegExp(String.raw`!3d${NUM}!4d${NUM}`),
    new RegExp(String.raw`[?&](?:q|ll|query|destination|daddr|center|sll)=(?:loc:)?${NUM}\s*,\s*\+?${NUM}`),
    new RegExp(String.raw`@${NUM},${NUM}`),
    new RegExp(String.raw`/(?:place|search|dir)/${NUM}\s*,\s*\+?${NUM}`),
    new RegExp(String.raw`geo:${NUM},${NUM}`),
    new RegExp(String.raw`^${NUM}\s*[,،]\s*${NUM}$`),
  ];
  for (const re of tries) {
    const m = s.match(re);
    if (m) {
      const p = valid(Number(m[1]), Number(m[2]));
      if (p) return p;
    }
  }
  return null;
}

export const googleMapsUrl = (p: MapPoint) => `https://www.google.com/maps/search/?api=1&query=${p.lat},${p.lng}`;
export const fmtPoint = (p: MapPoint) => `${p.lat.toFixed(6)}, ${p.lng.toFixed(6)}`;

export function isMapPoint(v: unknown): v is MapPoint {
  const p = v as MapPoint | null;
  return !!p && typeof p === 'object' && typeof p.lat === 'number' && typeof p.lng === 'number';
}

const MapLoading = ({ height }: { height: number | string }) => (
  <Box sx={{ height, borderRadius: 3, bgcolor: 'action.hover', display: 'grid', placeItems: 'center' }}>
    <CircularProgress size={28} />
  </Box>
);

/** The satellite map of one point (read-only), e.g. on a property's page. */
export function MapPointView({ point, map, height = 220, lang = 'ar' }: { point: MapPoint; map: MapSupport; height?: number; lang?: Lang }) {
  if (!map.token) return null;
  return (
    <Suspense fallback={<MapLoading height={height} />}>
      <MapCanvas token={map.token} mode="view" center={point} zoom={17} height={height} lang={lang} />
    </Suspense>
  );
}

/** Full-screen (phone) / large dialog: move the map until the pin is on the property. */
export function MapPickDialog({
  open, start, map, lang = 'ar', onClose, onPick,
}: { open: boolean; start: MapPoint | null; map: MapSupport; lang?: Lang; onClose: () => void; onPick: (p: MapPoint) => void }) {
  const theme = useTheme();
  const small = useMediaQuery(theme.breakpoints.down('sm'));
  const first = start ?? map.defaultCenter ?? AMMAN;
  const [center, setCenter] = useState<MapPoint>(first);
  if (!map.token) return null;
  return (
    <Dialog open={open} onClose={onClose} fullScreen={small} fullWidth maxWidth="md" TransitionProps={{ onEnter: () => setCenter(first) }}>
      <DialogTitle sx={{ pb: 0.5 }}>{T(lang, 'حدّد موقع العقار', 'Pin the property')}</DialogTitle>
      <Typography variant="body2" color="text.secondary" sx={{ px: 3, pb: 1.5 }}>
        {T(lang, 'حرّك الخريطة وقرّب حتى يصير الدبوس الأحمر فوق العقار تماماً.', 'Move and zoom the map until the red pin sits right on the property.')}
      </Typography>
      <DialogContent sx={{ p: { xs: 1, sm: 2 }, pt: '0 !important', height: small ? 'auto' : '65vh', display: 'flex' }}>
        {open && (
          <Suspense fallback={<MapLoading height="100%" />}>
            <MapCanvas token={map.token} mode="pick" locate center={first} zoom={start ? 17 : 12} bounds={start ? null : map.bbox ?? null} height="100%" onCenter={setCenter} lang={lang} />
          </Suspense>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary" dir="ltr" sx={{ flex: 1, fontVariantNumeric: 'tabular-nums' }}>{fmtPoint(center)}</Typography>
        <Button onClick={onClose}>{T(lang, 'إلغاء', 'Cancel')}</Button>
        <Button variant="contained" onClick={() => onPick(center)}>{T(lang, 'تأكيد الموقع', 'Use this location')}</Button>
      </DialogActions>
    </Dialog>
  );
}

/** The question itself: value {lat, lng} or undefined. */
export function MapPointInput({
  value, onChange, map, lang = 'ar', disabled,
}: { value: unknown; onChange: (v: MapPoint | undefined) => void; map: MapSupport | undefined; lang?: Lang; disabled?: boolean }) {
  const point = isMapPoint(value) ? value : null;
  const [linkOpen, setLinkOpen] = useState(false);
  const [link, setLink] = useState('');
  const [busy, setBusy] = useState<'link' | 'gps' | null>(null);
  const [note, setNote] = useState<{ kind: 'error' | 'warning'; text: string } | null>(null);
  const [picking, setPicking] = useState(false);
  const hasMap = !!map?.token;

  const fromLink = async (text: string) => {
    const t = text.trim();
    if (!t) return;
    setNote(null);
    const local = pointFromText(t);
    if (local) {
      onChange(local);
      setLinkOpen(false);
      setLink('');
      return;
    }
    if (!/^https?:\/\//i.test(t) || !map) {
      setNote({ kind: 'error', text: T(lang, 'هذا ليس رابط موقع. انسخ الرابط من خرائط Google (مشاركة ← نسخ الرابط).', 'That is not a location link. Copy it from Google Maps (Share → Copy link).') });
      return;
    }
    setBusy('link');
    try {
      const p = await map.resolveLink(t);
      if (p) {
        onChange(p);
        setLinkOpen(false);
        setLink('');
      } else {
        setNote({ kind: 'error', text: T(lang, 'لم نستطع قراءة الموقع من هذا الرابط. جرّب «تحديد على الخريطة».', 'Could not read a location from this link. Try «Pick on map».') });
      }
    } catch {
      setNote({ kind: 'error', text: T(lang, 'تعذّر فتح الرابط الآن. حاول مرة ثانية.', 'Could not open the link right now. Please try again.') });
    } finally {
      setBusy(null);
    }
  };

  const fromGps = () => {
    setNote(null);
    if (!('geolocation' in navigator)) {
      setNote({ kind: 'error', text: T(lang, 'هذا الجهاز لا يدعم تحديد الموقع.', 'This device cannot share its location.') });
      return;
    }
    setBusy('gps');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setBusy(null);
        const p = valid(pos.coords.latitude, pos.coords.longitude);
        if (!p) return;
        onChange(p);
        const acc = Math.round(pos.coords.accuracy);
        if (acc > 60) {
          setNote({
            kind: 'warning',
            text: T(lang, `الموقع تقريبي (±${acc} م). تأكد من الدبوس، وعدّله على الخريطة إذا لزم.`, `Approximate location (±${acc} m). Check the pin and adjust it on the map if needed.`),
          });
        }
      },
      (e) => {
        setBusy(null);
        setNote({
          kind: 'error',
          text: e.code === e.PERMISSION_DENIED
            ? T(lang, 'لم يُسمح بالوصول للموقع. اسمح للموقع من إعدادات المتصفح، أو استخدم رابطاً أو الخريطة.', 'Location access was blocked. Allow it in the browser settings, or use a link or the map.')
            : T(lang, 'تعذّر معرفة موقعك الآن. جرّب مرة ثانية أو استخدم الخريطة.', 'Could not get your location. Try again or use the map.'),
        });
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };

  return (
    <Stack spacing={1.25}>
      {point && (
        <Box>
          {hasMap ? (
            <MapPointView point={point} map={map!} lang={lang} height={200} />
          ) : null}
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: hasMap ? 1 : 0 }}>
            <Typography variant="body2" dir="ltr" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>{fmtPoint(point)}</Typography>
            <Box sx={{ flex: 1 }} />
            <Button size="small" startIcon={<OpenInNewIcon />} href={googleMapsUrl(point)} target="_blank" rel="noopener noreferrer">
              {T(lang, 'خرائط Google', 'Google Maps')}
            </Button>
            {!disabled && hasMap && (
              <Button size="small" startIcon={<EditLocationAltIcon />} onClick={() => setPicking(true)}>{T(lang, 'تعديل', 'Adjust')}</Button>
            )}
            {!disabled && (
              <Button size="small" color="inherit" onClick={() => { onChange(undefined); setNote(null); }}>{T(lang, 'إزالة', 'Remove')}</Button>
            )}
          </Stack>
        </Box>
      )}

      {!point && !disabled && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button variant="outlined" startIcon={<LinkIcon />} onClick={() => { setLinkOpen((o) => !o); setNote(null); }} sx={{ flex: 1 }}>
            {T(lang, 'لصق رابط الموقع', 'Paste a location link')}
          </Button>
          <Button variant="outlined" startIcon={busy === 'gps' ? <CircularProgress size={16} /> : <MyLocationIcon />} disabled={busy === 'gps'} onClick={fromGps} sx={{ flex: 1 }}>
            {T(lang, 'موقعي الحالي', 'My current location')}
          </Button>
          {hasMap && (
            <Button variant="outlined" startIcon={<EditLocationAltIcon />} onClick={() => { setPicking(true); setNote(null); }} sx={{ flex: 1 }}>
              {T(lang, 'تحديد على الخريطة', 'Pick on the map')}
            </Button>
          )}
        </Stack>
      )}

      {linkOpen && !point && (
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <TextField
            fullWidth
            size="small"
            autoFocus
            value={link}
            placeholder={T(lang, 'الصق رابط خرائط Google أو الإحداثيات', 'Paste a Google Maps link or coordinates')}
            slotProps={{ htmlInput: { dir: 'ltr' } }}
            onChange={(e) => setLink(e.target.value)}
            onPaste={(e) => {
              const t = e.clipboardData.getData('text');
              if (t) {
                e.preventDefault();
                setLink(t);
                void fromLink(t);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void fromLink(link);
              }
            }}
          />
          <Button variant="contained" disabled={!link.trim() || busy === 'link'} onClick={() => void fromLink(link)} sx={{ minWidth: 72 }}>
            {busy === 'link' ? <CircularProgress size={18} color="inherit" /> : T(lang, 'تم', 'OK')}
          </Button>
        </Stack>
      )}

      {note && <Alert severity={note.kind} onClose={() => setNote(null)}>{note.text}</Alert>}

      {map && hasMap && (
        <MapPickDialog
          open={picking}
          start={point}
          map={map}
          lang={lang}
          onClose={() => setPicking(false)}
          onPick={(p) => {
            onChange(p);
            setPicking(false);
            setNote(null);
          }}
        />
      )}
    </Stack>
  );
}
